import { useEffect, useState } from 'react'
import { Check, Clock, ExternalLink, Pencil, X } from 'lucide-react'
import api from '../services/api'

// Painel lateral "Análise da média" (painel do Combustível → Analisar), igual ao modelo do usuário:
// resumo, inconsistências automáticas, abastecimentos usados no cálculo e as duas conclusões.

interface Abast {
  dpstip: string; dpsnum: number; dpsdesnum: number; data: string; hora: number; litros: number; hodometro: number
  media: number; km: number | null; posto: string; motorista: string; acerto: boolean
}
interface Detalhe {
  veicod: string; placa: string; tipo: string; prevista: number; media: number; diferenca: number | null
  mediaAbastecimento?: { data: string; hora: number } | null // abastecimento da média exibida
  abastecimentos: Abast[]; ultimos30?: Abast[]; inconsistencias: string[]; motivos: string[]
}

const br = (s: string) => s.split('-').reverse().join('/')
const fmt = (v: number, c = 0) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c })
const fmtHora = (h: number) => { const [hh, mm = '00'] = h.toFixed(2).split('.'); return `${hh.padStart(2, '0')}:${mm}` }
const chave = (a: Abast) => `${a.dpstip}|${a.dpsnum}|${a.dpsdesnum}`

const rotulo: React.CSSProperties = { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#334155', margin: '0.7rem 0 0.3rem' }
const campo: React.CSSProperties = { width: '100%', padding: '0.5rem', border: '1px solid #d5e0e8', borderRadius: 7, fontSize: '0.84rem', boxSizing: 'border-box', background: '#fff' }

export default function AnaliseMediaDrawer({ veicod, abast = '', dataIni, dataFim, onFechar, onConcluido, onAbrirLancamentos }: {
  veicod: string; abast?: string; dataIni: string; dataFim: string
  onFechar: () => void; onConcluido: (msg: string) => void; onAbrirLancamentos: () => void
}) {
  const [det, setDet] = useState<Detalhe | null>(null)
  const [erro, setErro] = useState('')
  const [modo, setModo] = useState<'' | 'ok' | 'erro'>('')
  const [motivo, setMotivo] = useState('')
  const [obs, setObs] = useState('')
  const [campoErro, setCampoErro] = useState<'hodometro' | 'litros'>('hodometro')
  const [abastSel, setAbastSel] = useState('')
  const [novoValor, setNovoValor] = useState('')
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    api.get(`/combustivel/analise-media/${encodeURIComponent(veicod)}`, { params: { dataIni, dataFim, abast } })
      .then(r => {
        setDet(r.data)
        const editaveis = (r.data.abastecimentos as Abast[]).filter(a => !a.acerto)
        // já deixa selecionado o abastecimento da média divergente (o provável lançamento errado)
        const ma = r.data.mediaAbastecimento as { data: string; hora: number } | null
        const alvo = ma ? editaveis.find(a => a.data === ma.data && Number(a.hora) === Number(ma.hora)) : undefined
        if (editaveis.length) setAbastSel(chave(alvo ?? editaveis[editaveis.length - 1]))
      })
      .catch(e => setErro(e?.response?.data?.error || 'Erro ao carregar a análise.'))
  }, [veicod, abast, dataIni, dataFim])

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar() }
    window.addEventListener('keydown', esc); document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', esc); document.body.style.overflow = '' }
  }, [onFechar])

  const sel = det?.abastecimentos.find(a => chave(a) === abastSel)
  const valorAtual = sel ? (campoErro === 'litros' ? fmt(sel.litros, 3) : fmt(sel.hodometro)) : ''

  async function concluir() {
    if (!det) return
    setErro('')
    const corpo: any = { veicod, abast, dataIni, dataFim, observacao: obs }
    if (modo === 'ok') {
      if (!motivo) { setErro('Selecione o motivo da divergência.'); return }
      Object.assign(corpo, { conclusao: 'C', motivo })
    } else {
      const novo = Number(novoValor.replace(/\./g, '').replace(',', '.'))
      if (!sel) { setErro('Escolha o abastecimento.'); return }
      if (!(novo > 0)) { setErro('Informe o novo valor.'); return }
      Object.assign(corpo, { conclusao: 'E', correcao: { dpstip: sel.dpstip, dpsnum: sel.dpsnum, dpsdesnum: sel.dpsdesnum, campo: campoErro, novoValor: novo } })
    }
    setSalvando(true)
    try {
      await api.post('/combustivel/analise-media', corpo)
      onConcluido(modo === 'ok' ? `Análise do veículo ${veicod} concluída: lançamento considerado correto.` : `Lançamento do veículo ${veicod} corrigido e média recalculada.`)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao concluir a análise.')
    } finally { setSalvando(false) }
  }

  // Marca como "Em análise" (conclusao A): continua na lista com o botão laranja
  async function aguardarMotorista() {
    setErro(''); setSalvando(true)
    try {
      await api.post('/combustivel/analise-media', { veicod, abast, dataIni, dataFim, conclusao: 'A', observacao: obs })
      onConcluido(`Informações solicitadas ao motorista. Análise do veículo ${veicod} salva e aguardando retorno.`)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar a análise.')
    } finally { setSalvando(false) }
  }

  const resumo = (titulo: string, valor: string, cor = '#18324c') => (
    <div style={{ background: '#f4f8fb', borderRadius: 8, padding: '0.6rem 0.7rem' }}>
      <div style={{ fontSize: '0.66rem', color: '#6e8296' }}>{titulo}</div>
      <div style={{ fontWeight: 800, marginTop: 3, color: cor, fontSize: '1rem' }}>{valor}</div>
    </div>
  )

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onFechar() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(4,24,41,.45)', zIndex: 60 }}>
      <aside style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 'min(580px, 96vw)', background: '#fff', boxShadow: '-10px 0 30px rgba(0,0,0,.2)', overflowY: 'auto', animation: 'amEntra .2s ease-out' }}>
        <style>{`@keyframes amEntra { from { transform: translateX(40px); opacity: .4 } to { transform: none; opacity: 1 } }`}</style>
        <div style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2, padding: '1rem 1.2rem', borderBottom: '1px solid #dfe7ef', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <div style={{ margin: 0, color: '#123f68', fontWeight: 800, fontSize: '1.05rem' }}>Análise da média • Veículo {veicod}</div>
            <div style={{ fontSize: '0.76rem', color: '#6e8296' }}>{det ? `Placa ${det.placa} • ` : ''}Período {br(dataIni)} a {br(dataFim)}</div>
          </div>
          <button onClick={onFechar} title="Fechar (Esc)" style={{ border: 'none', background: '#eef3f7', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><X size={16} /></button>
        </div>

        <div style={{ padding: '1rem 1.2rem 1.5rem' }}>
          {!det && !erro && <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Carregando...</div>}
          {det && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: '0.9rem' }}>
                {resumo(det.mediaAbastecimento ? `Média do abastecimento ${br(det.mediaAbastecimento.data).slice(0, 5)}` : 'Média do abastecimento', det.media ? `${fmt(det.media, 2)} km/L` : '—')}
                {resumo('Média prevista', det.prevista ? `${fmt(det.prevista, 2)} km/L` : '—')}
                {resumo('Diferença', det.diferenca != null ? `${det.diferenca > 0 ? '+' : ''}${fmt(det.diferenca, 1)}%` : '—', det.diferenca == null ? '#18324c' : det.diferenca < 0 ? '#db3545' : '#18a957')}
              </div>

              {det.inconsistencias.length > 0 ? (
                <div style={{ background: '#fff3f4', borderLeft: '4px solid #db3545', padding: '0.7rem 0.8rem', borderRadius: 6, fontSize: '0.8rem', marginBottom: '0.9rem' }}>
                  <b>Possível inconsistência identificada</b>
                  <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.1rem' }}>
                    {det.inconsistencias.map((t, i) => <li key={i} style={{ marginTop: 2 }}>{t}</li>)}
                  </ul>
                </div>
              ) : (
                <div style={{ background: '#f0f9f4', borderLeft: '4px solid #18a957', padding: '0.6rem 0.8rem', borderRadius: 6, fontSize: '0.8rem', marginBottom: '0.9rem', color: '#166534' }}>
                  Nenhuma inconsistência automática encontrada nos lançamentos — verifique com o motorista/abastecedor.
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0.4rem 0 0.5rem' }}>
                <b style={{ fontSize: '0.85rem', color: '#163f64' }}>Abastecimentos utilizados no cálculo</b>
                <button onClick={onAbrirLancamentos} style={{ border: 'none', background: 'none', color: '#1a6ea8', fontWeight: 700, fontSize: '0.74rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  Abrir no Atualizar Lançamentos <ExternalLink size={12} />
                </button>
              </div>
              <div style={{ border: '1px solid #dfe7ef', borderRadius: 8, overflow: 'hidden' }}>
                {det.abastecimentos.length === 0 && <div style={{ padding: '0.7rem', fontSize: '0.8rem', color: '#94a3b8' }}>Nenhum abastecimento no período.</div>}
                {det.abastecimentos.map(a => (
                  <div key={chave(a)} style={{ display: 'grid', gridTemplateColumns: '92px 1fr 80px', gap: 8, padding: '0.55rem 0.7rem', borderBottom: '1px solid #eef2f6', fontSize: '0.76rem', alignItems: 'center' }}>
                    <div><b>{br(a.data)}</b><div style={{ color: '#94a3b8', fontSize: '0.68rem' }}>{fmtHora(a.hora)} · {a.posto}</div></div>
                    <span style={{ color: '#475569' }}>{fmt(a.litros, 3)} L • Hodômetro {fmt(a.hodometro)}{a.km != null ? ` • ${fmt(a.km)} km` : ''}</span>
                    <b style={{ textAlign: 'right', color: det.prevista && a.media > 0 && a.media < det.prevista ? '#db3545' : '#18324c' }}>{a.media > 0 ? `${fmt(a.media, 2)} km/L` : '—'}</b>
                  </div>
                ))}
              </div>

              {/* Abastecimentos dos últimos 30 dias (independe do período do painel) */}
              {det.ultimos30 && (
                <details open className="am-30" style={{ marginTop: 14, border: '1px solid #dfe7ef', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
                  <style>{`.am-30 summary::-webkit-details-marker { display: none } .am-30 summary::before { content: "▾"; margin-right: 7px; color: #0e6fab } .am-30:not([open]) summary::before { content: "▸" }`}</style>
                  <summary style={{ cursor: 'pointer', listStyle: 'none', display: 'flex', alignItems: 'center', padding: '11px 13px', background: '#f7f9fb', color: '#163f64', fontSize: '0.8rem', fontWeight: 800 }}>
                    <span style={{ flex: 1 }}>Abastecimentos dos últimos 30 dias</span>
                    <small style={{ color: '#6e8296', fontSize: '0.68rem', fontWeight: 700 }}>
                      {det.ultimos30.length} {det.ultimos30.length === 1 ? 'abastecimento' : 'abastecimentos'} • {fmt(det.ultimos30.reduce((s, a) => s + Number(a.litros || 0), 0), 1)} L
                    </small>
                  </summary>
                  <div style={{ display: 'grid', gridTemplateColumns: '92px 1fr 80px', gap: 8, padding: '7px 0.7rem', borderTop: '1px solid #dfe7ef', borderBottom: '1px solid #dfe7ef', color: '#6e8296', fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase' }}>
                    <span>Data</span><span>Litros / Hodômetro</span><span style={{ textAlign: 'right' }}>Média</span>
                  </div>
                  <div style={{ maxHeight: 186, overflowY: 'auto' }}>
                    {det.ultimos30.length === 0 && <div style={{ padding: '0.7rem', fontSize: '0.8rem', color: '#94a3b8' }}>Nenhum abastecimento nos últimos 30 dias.</div>}
                    {det.ultimos30.map(a => (
                      <div key={chave(a)} style={{ display: 'grid', gridTemplateColumns: '92px 1fr 80px', gap: 8, padding: '0.55rem 0.7rem', borderBottom: '1px solid #eef2f6', fontSize: '0.76rem', alignItems: 'center' }}>
                        <div><b>{br(a.data)}</b><div style={{ color: '#94a3b8', fontSize: '0.68rem' }}>{fmtHora(a.hora)} · {a.posto}</div></div>
                        <span style={{ color: '#475569' }}>{fmt(a.litros, 3)} L • Hodômetro {fmt(a.hodometro)}{a.km != null ? ` • ${fmt(a.km)} km` : ''}</span>
                        <b style={{ textAlign: 'right', color: det.prevista && a.media > 0 && a.media < det.prevista ? '#db3545' : '#18324c' }}>{a.media > 0 ? `${fmt(a.media, 2)} km/L` : '—'}</b>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: '1.1rem' }}>
                <button onClick={() => { setModo('ok'); setErro('') }}
                  style={{ padding: '0.75rem', borderRadius: 8, fontWeight: 800, cursor: 'pointer', border: '1px solid #18a957', background: modo === 'ok' ? '#d9f5e5' : '#effbf4', color: '#16884a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Check size={16} /> Lançamento está correto
                </button>
                <button onClick={() => { setModo('erro'); setErro('') }}
                  style={{ padding: '0.75rem', borderRadius: 8, fontWeight: 800, cursor: 'pointer', border: '1px solid #db3545', background: modo === 'erro' ? '#ffe3e6' : '#fff3f4', color: '#c62e3d', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Pencil size={15} /> Identifiquei um erro
                </button>
              </div>
              <button onClick={aguardarMotorista} disabled={salvando} title="Salvar análise aguardando retorno do motorista"
                style={{ width: '100%', marginTop: 10, border: '1px solid #d59a25', background: '#fff9e9', color: '#8b5b00', borderRadius: 8, padding: '0.7rem 0.9rem', display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', textAlign: 'left' }}>
                <Clock size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>
                  <b style={{ display: 'block', fontSize: '0.8rem' }}>Solicitado informações ao motorista, aguardando retorno</b>
                  <small style={{ display: 'block', fontSize: '0.68rem', color: '#8b6b2c', marginTop: 3 }}>Salva a análise em andamento para continuar após o retorno do motorista</small>
                </span>
              </button>
              <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 7, background: '#f5f8fa', color: '#60778a', fontSize: '0.7rem', lineHeight: 1.45 }}>
                <b style={{ color: '#395770' }}>Como funciona o status:</b> o veículo permanece como{' '}
                <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 10, background: '#fff0f2', color: '#d32c41', fontWeight: 800 }}>Nova</span>{' '}
                até que esta análise seja salva para continuar depois. Depois de salva, fica como{' '}
                <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 10, background: '#fff4df', color: '#a66000', fontWeight: 800 }}>Em análise</span>{' '}
                na tela principal até ser concluída.
              </div>

              {modo === 'ok' && (
                <div style={{ marginTop: '1rem', borderTop: '1px solid #dfe7ef', paddingTop: '0.8rem' }}>
                  <b style={{ fontSize: '0.85rem', color: '#163f64' }}>Concluir como lançamento correto</b>
                  <label style={rotulo}>Motivo da divergência *</label>
                  <select value={motivo} onChange={e => setMotivo(e.target.value)} style={campo}>
                    <option value="">Selecione...</option>
                    {det.motivos.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <label style={rotulo}>Observação</label>
                  <textarea rows={3} maxLength={500} value={obs} onChange={e => setObs(e.target.value)} placeholder="Descreva brevemente o motivo identificado..." style={{ ...campo, resize: 'vertical' }} />
                </div>
              )}

              {modo === 'erro' && (
                <div style={{ marginTop: '1rem', borderTop: '1px solid #dfe7ef', paddingTop: '0.8rem' }}>
                  <b style={{ fontSize: '0.85rem', color: '#163f64' }}>Corrigir lançamento</b>
                  <label style={rotulo}>Abastecimento</label>
                  <select value={abastSel} onChange={e => { setAbastSel(e.target.value); setNovoValor('') }} style={campo}>
                    {det.abastecimentos.filter(a => !a.acerto).map(a => (
                      <option key={chave(a)} value={chave(a)}>{br(a.data)} {fmtHora(a.hora)} · {fmt(a.litros, 3)} L · hodômetro {fmt(a.hodometro)} · reg {a.dpsnum}</option>
                    ))}
                  </select>
                  <label style={rotulo}>Campo com erro</label>
                  <select value={campoErro} onChange={e => { setCampoErro(e.target.value as 'hodometro' | 'litros'); setNovoValor('') }} style={campo}>
                    <option value="hodometro">Hodômetro</option>
                    <option value="litros">Quantidade abastecida (litros)</option>
                  </select>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={rotulo}>{campoErro === 'litros' ? 'Litros atuais' : 'Hodômetro atual'}</label>
                      <input value={valorAtual} readOnly tabIndex={-1} style={{ ...campo, background: '#f3f4f6', borderColor: '#f3f4f6' }} />
                    </div>
                    <div>
                      <label style={rotulo}>{campoErro === 'litros' ? 'Litros corretos *' : 'Novo hodômetro *'}</label>
                      <input value={novoValor} onChange={e => setNovoValor(e.target.value.replace(/[^\d,.]/g, ''))} inputMode="decimal" autoFocus style={campo} />
                    </div>
                  </div>
                  <label style={rotulo}>Observação</label>
                  <textarea rows={2} maxLength={500} value={obs} onChange={e => setObs(e.target.value)} placeholder="Informe o motivo da correção..." style={{ ...campo, resize: 'vertical' }} />
                  <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 6 }}>
                    A correção usa a mesma regra do lápis do Atualizar Lançamentos: recalcula o valor{campoErro === 'litros' ? ', o estoque do depósito' : ''} e a média.
                  </div>
                </div>
              )}
            </>
          )}

          {erro && <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.8rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b' }}>{erro}</div>}

          {modo && det && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '0.9rem' }}>
              <button onClick={() => { setModo(''); setErro('') }} className="btn btn-secondary">Cancelar</button>
              <button onClick={concluir} disabled={salvando} className="btn"
                style={{ background: '#18a957', color: '#fff', fontWeight: 800 }}>
                {salvando ? 'Salvando...' : modo === 'ok' ? 'Concluir análise' : 'Salvar correção'}
              </button>
            </div>
          )}
        </div>
      </aside>
    </div>
  )
}
